/* ══════════════════════════════════════════════════════════════
   Priority Feed — Scored News List with "Why It Matters"
   For Intelligence page Priority Feed tab
   ══════════════════════════════════════════════════════════════ */

import { fetchLLMPriorityFeed } from '../services/llm-api';
import { renderUnavailableBlock } from './data-state';

export interface PriorityItem {
  title: string;
  score: number;
  category: string;
  impact: string;
  source: string;
  timeAgo: string;
  entities: string[];
  whyItMatters: string;
}

function mapFeedItem(item: {
  title?: string;
  priorityScore?: number;
  category?: string;
  impact?: string;
  source?: string;
  timeAgo?: string;
  entities?: { companies?: string[]; sectors?: string[]; indices?: string[] };
  description?: string;
}): PriorityItem {
  const entities = [
    ...(item.entities?.companies || []),
    ...(item.entities?.sectors || []),
    ...(item.entities?.indices || []),
  ].slice(0, 4);

  return {
    title: item.title || '',
    score: item.priorityScore || 0,
    category: item.category || 'general',
    impact: item.impact || 'medium',
    source: item.source || 'Unknown',
    timeAgo: item.timeAgo || '',
    entities,
    whyItMatters: item.description || 'Live explanation unavailable',
  };
}

const CAT_COLORS: Record<string, string> = {
  policy: '#ef4444', earnings: '#22c55e', markets: '#3b82f6', ipo: '#a855f7',
  commodities: '#f59e0b', banking: '#0ea5e9', tech: '#8b5cf6', global: '#6366f1',
  corporate: '#ec4899', auto: '#14b8a6', pharma: '#06b6d4', energy: '#f97316',
  currency: '#84cc16', macro: '#d946ef', tax: '#0284c7',
};

export function renderPriorityFeed(container: HTMLElement, items?: PriorityItem[]): void {
  if (items) {
    renderWithData(container, items);
  } else {
    container.innerHTML = `<div style="padding: 2rem; text-align: center;">Loading LLM priority feed...</div>`;
    fetchLLMPriorityFeed().then(feed => {
      if (feed?.length > 0) {
        renderWithData(container, feed.map(mapFeedItem));
      } else {
        container.innerHTML = renderUnavailableBlock('priority-feed', 'Live priority feed unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('priority-feed', 'Live priority feed unavailable');
    });
  }
}

function renderWithData(container: HTMLElement, data: PriorityItem[]): void {

  const scoreColor = (s: number) => s >= 80 ? 'var(--india-red)' : s >= 60 ? 'var(--india-saffron)' : 'var(--india-green)';

  const feedItems = data.map(item => `
    <div class="priority-item glass-card">
      <div class="priority-score-col">
        <div class="priority-score" style="border-color: ${scoreColor(item.score)}">
          <span class="score-num">${item.score}</span>
        </div>
      </div>
      <div class="priority-content">
        <div class="priority-header">
          <span class="priority-category" style="background:${CAT_COLORS[item.category] || '#666'}20; color:${CAT_COLORS[item.category] || '#666'}">${item.category}</span>
          ${item.entities.map(e => `<span class="entity-badge">${e}</span>`).join('')}
          <span class="priority-meta">${item.source} · ${item.timeAgo}</span>
        </div>
        <h4 class="priority-title">${item.title}</h4>
        <p class="priority-why">💡 ${item.whyItMatters}</p>
      </div>
    </div>`).join('');

  container.innerHTML = `
    <div class="priority-feed">
      <div class="feed-header">
        <h3>📰 Priority Feed</h3>
        <div class="feed-filters">
          <button class="filter-btn active">All</button>
          <button class="filter-btn">Score 80+</button>
          <button class="filter-btn">Policy</button>
          <button class="filter-btn">Earnings</button>
        </div>
      </div>
      <div class="feed-list">${feedItems}</div>
    </div>`;
}
